import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';
import SettingsCompanyProfile from './SettingsCompanyProfile';
import SettingsImportData from './SettingsImportData';
import SettingsListCreation from './SettingsListCreation';
import SettingsAIConfig from './SettingsAIConfig';
import SettingsPartsCategories from './SettingsPartsCategories';
import SettingsEmployeeStatus from './SettingsEmployeeStatus';
import SettingsEmployeeDepartments from './SettingsEmployeeDepartments';
import SettingsPPMConfig from './SettingsPPMConfig';
import SettingsPPMPaymentTerms from './SettingsPPMPaymentTerms';
import SettingsPPMDiscountTiers from './SettingsPPMDiscountTiers';
import SettingsPPMPricing from './SettingsPPMPricing';
import SettingsNumbers from './SettingsNumbers';
import SettingsTextTemplates from './SettingsTextTemplates';
import SettingsModules from './SettingsModules';
import SettingsUsers from './SettingsUsers';
import SettingsRoles from './SettingsRoles';

function Settings({ defaultSection }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [activeSection, setActiveSection] = useState(defaultSection || 'company-profile');
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchCompanyInfo();
    // Get user role from localStorage
    const storedUser = localStorage.getItem('doorbase_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserRole(user.role_name || '');
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }
  }, []);

  // Auto-switch tab when defaultSection targets a specific section
  useEffect(() => {
    if (defaultSection) {
      for (const [tabId, tabSections] of Object.entries(sections)) {
        if (tabSections.find(s => s.id === defaultSection)) {
          setActiveTab(tabId);
          setActiveSection(defaultSection);
          break;
        }
      }
    }
  }, [defaultSection]);

  const fetchCompanyInfo = async () => {
    try {
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();
      setCompanyName(data.company_name || 'DoorBase');
      setCompanyLogo(data.logo_url || '');
    } catch (err) {
      console.error('Error fetching company info:', err);
      setCompanyName('DoorBase');
    }
  };

  // Filter tabs based on user role
  const allTabs = [
    { id: 'general', label: '📋 General' },
    { id: 'operations', label: '🔧 Operations' },
    { id: 'dispatch', label: '📅 Dispatch' },
    { id: 'finance', label: '💰 Finance' },
    { id: 'numbers', label: '🔢 Numbers' },
    { id: 'hr', label: '👥 HR' },
    { id: 'data', label: '📊 Data', adminOnly: true }, // Admin only
  ];

  // Show all tabs for Admin, hide admin-only tabs for others
  const tabs = allTabs.filter(tab => {
    if (tab.adminOnly) {
      return userRole === 'Admin';
    }
    return true;
  });

  const sections = {
    general: [
      { id: 'company-profile', label: 'Company Profile', component: SettingsCompanyProfile },
      { id: 'text-templates', label: 'Note Templates', component: SettingsTextTemplates },
      { id: 'templates', label: 'Document Templates', link: '/settings/templates' },
      { id: 'email-templates', label: 'Email Templates', link: '/email-templates' },
      { id: 'page-settings', label: 'Page Settings', link: '/settings/page-settings' },
      { id: 'modules', label: 'Optional Modules', component: SettingsModules },
      { id: 'users', label: 'Users & Access', component: SettingsUsers },
      { id: 'roles', label: 'Roles & Permissions', component: SettingsRoles },
      { id: 'preferences', label: 'System Preferences', component: null, comingSoon: true },
    ],
    operations: [
      { id: 'parts-categories', label: 'Parts Categories', component: SettingsPartsCategories },
      { id: 'list-creation', label: 'Custom Lists', component: SettingsListCreation },
    ],
    dispatch: [
      { id: 'dispatch-categories', label: 'Dispatch Categories', link: '/settings/dispatch-categories' },
      { id: 'job-status-colors', label: 'Job Status Colors', link: '/settings/job-status-colors' },
      { id: 'absence-types', label: 'Absence Types', component: null, comingSoon: true },
    ],
    finance: [
      { id: 'nominal-codes', label: 'Nominal Codes (Chart of Accounts)', link: '/settings/nominal-codes' },
      { id: 'cis-reverse-vat', label: 'CIS & Reverse VAT', link: '/settings/cis-reverse-vat' },
      { id: 'ppm-payment-terms', label: 'PPM Payment Terms', component: SettingsPPMPaymentTerms },
      { id: 'ppm-discount-tiers', label: 'PPM Discount Tiers', component: SettingsPPMDiscountTiers },
      { id: 'ppm-pricing', label: 'PPM Pricing', component: SettingsPPMPricing },
    ],
    numbers: [
      { id: 'numbers-all', label: 'Number Formats', component: SettingsNumbers },
      { id: 'pricing', label: 'Pricing Settings (Dual Pricing)', route: '/settings/pricing' },
    ],
    hr: [
      { id: 'employee-status', label: 'Employee Status', component: SettingsEmployeeStatus },
      { id: 'departments', label: 'Departments', component: SettingsEmployeeDepartments },
      { id: 'leave-settings', label: 'Leave Settings', component: null, comingSoon: true },
    ],
    data: [
      { id: 'import-data', label: 'Import Data', component: SettingsImportData },
      { id: 'export-data', label: 'Export Data', component: null, comingSoon: true },
      { id: 'ai-config', label: 'AI Configuration', component: SettingsAIConfig },
    ],
  };

  const currentSections = sections[activeTab] || [];
  const currentSection = currentSections.find(s => s.id === activeSection);
  const CurrentComponent = currentSection?.component;

  // Handle section click
  const handleSectionClick = (section) => {
    if (section.link) {
      navigate(section.link);
    } else if (!section.comingSoon) {
      setActiveSection(section.id);
    }
  };

  // When tab changes, auto-select first section
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const firstSection = sections[tabId]?.[0];
    if (firstSection && !firstSection.comingSoon && !firstSection.link) {
      setActiveSection(firstSection.id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader
        title="Settings"
        subtitle="Configure your DoorBase system"
        backButton
        onBack={() => navigate('/admin')}
        backTitle="Back to Admin"
        sticky
      />

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[116px] z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 py-3 font-semibold text-lg transition relative text-center ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">

          {/* Sidebar - Section Menu */}
          <div className="md:col-span-1">
            <div className="bg-blue-50 rounded-lg shadow-sm border border-gray-200 p-3 sticky top-4">
              {/* Company Logo */}
              {companyLogo && (
                <div className="flex justify-center mb-2">
                  <img src={companyLogo} alt="Company Logo" className="h-12 object-contain" />
                </div>
              )}
              <div className="border-b border-gray-300 mb-3"></div>

              {/* Section Title */}
              <div className="text-lg font-bold text-gray-900 mb-3 text-center">
                {tabs.find(t => t.id === activeTab)?.label.replace(/^.+?\s/, '')}
              </div>
              <nav className="space-y-1 max-h-96 overflow-y-auto">
                {currentSections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section)}
                    disabled={section.comingSoon && !section.link}
                    title={section.label}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition truncate ${
                      activeSection === section.id && !section.link
                        ? 'bg-gray-300 text-black font-medium'
                        : section.comingSoon
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="truncate block">{section.label}</span>
                    {section.comingSoon && (
                      <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">Soon</span>
                    )}
                    {section.link && (
                      <span className="ml-2 text-xs">→</span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {CurrentComponent ? (
              <CurrentComponent />
            ) : currentSection?.comingSoon ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-6xl mb-4">🚧</div>
                <h2 className="text-xl font-medium text-gray-900 mb-2">Coming Soon</h2>
                <p className="text-gray-600">
                  {currentSection.label} settings will be available in a future update.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-6xl mb-4">⚙️</div>
                <h2 className="text-xl font-medium text-gray-900 mb-2">Settings</h2>
                <p className="text-gray-600">
                  Select a section from the menu to configure your system.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
